import { Calendar, Clock, LogOut, Mail, Shield, ShieldCheck, User } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { type AdminUserSummary, useAdminUser } from "@/lib/api";
import { formatActionLabel, formatDate, formatDateTime, formatDetail } from "@/lib/format";

interface UserDetailDialogProps {
  user: AdminUserSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the "Sign out everywhere" confirmation; the button is hidden when unset. */
  onRevokeSessions?: (user: AdminUserSummary) => void;
}

function getInitials(user: AdminUserSummary): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
  }
  if (user.full_name) {
    return user.full_name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return (user.email[0] ?? "?").toUpperCase();
}

export function UserDetailDialog({
  user,
  open,
  onOpenChange,
  onRevokeSessions,
}: UserDetailDialogProps) {
  const { data: detail } = useAdminUser(user?.id ?? null, open);

  // Loading is derived: we're open with a user but detail hasn't arrived yet.
  const loading = open && !!user && !detail;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:!max-w-2xl">
        <DialogHeader className="sr-only">
          <DialogTitle>{user?.full_name || user?.email}</DialogTitle>
          <DialogDescription>User details</DialogDescription>
        </DialogHeader>

        <DialogBody className="pt-6">
          {loading ? (
            <div className="text-muted py-12 text-center text-sm">Loading...</div>
          ) : detail && user ? (
            <div className="space-y-5">
              {/* Profile header */}
              <div className="flex items-center gap-4 pr-8">
                <Avatar size="lg">
                  <AvatarFallback className="bg-accent-light text-primary text-sm font-semibold">
                    {getInitials(user)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <h3 className="text-foreground truncate text-base font-bold">
                    {detail.full_name || detail.email.split("@")[0]}
                  </h3>
                  <p className="text-muted truncate text-sm">{detail.email}</p>
                </div>
                <Badge
                  variant={detail.role === "admin" ? "default" : "secondary"}
                  className="shrink-0"
                >
                  {detail.role === "admin" ? (
                    <ShieldCheck className="mr-1 size-3" />
                  ) : (
                    <User className="mr-1 size-3" />
                  )}
                  {detail.role}
                </Badge>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="details">
                <TabsList variant="line" className="w-full justify-start">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="activity">
                    Activity ({detail.activity_logs.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 pt-4">
                  {/* Info rows */}
                  <div className="space-y-3">
                    <DetailRow icon={User} label="Name">
                      {detail.first_name || detail.last_name
                        ? `${detail.first_name ?? ""} ${detail.last_name ?? ""}`.trim()
                        : "—"}
                    </DetailRow>

                    <DetailRow icon={Mail} label="Email">
                      {detail.email}
                      {detail.email_verified ? (
                        <Badge
                          variant="outline"
                          className="ml-2 border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300"
                        >
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="ml-2">
                          Unverified
                        </Badge>
                      )}
                    </DetailRow>

                    <DetailRow icon={Shield} label="Role">
                      {detail.role === "admin" ? "Administrator" : "User"}
                    </DetailRow>
                  </div>

                  {/* Dates section */}
                  <div className="border-border rounded-lg border p-3">
                    <h4 className="text-foreground mb-2 text-xs font-medium">Timeline</h4>
                    <div className="space-y-2">
                      <DateRow
                        icon={Calendar}
                        label="Joined"
                        value={formatDate(detail.created_at)}
                      />
                      <DateRow
                        icon={Clock}
                        label="Last updated"
                        value={formatDate(detail.updated_at)}
                      />
                      <DateRow
                        icon={Clock}
                        label="Last active"
                        value={formatDate(detail.last_active_at)}
                      />
                    </div>
                  </div>

                  {onRevokeSessions && (
                    <div className="flex justify-end">
                      <Button size="sm" variant="outline" onClick={() => onRevokeSessions(user)}>
                        <LogOut className="size-4" />
                        Sign out everywhere
                      </Button>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="activity" className="pt-4">
                  {detail.activity_logs.length === 0 ? (
                    <p className="text-muted py-8 text-center text-sm">No activity recorded yet.</p>
                  ) : (
                    <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
                      {detail.activity_logs.map((entry) => {
                        const detailStr = formatDetail(entry.detail);
                        return (
                          <div
                            key={entry.id}
                            className="hover:bg-secondary/60 flex items-start gap-3 rounded-md px-2 py-2 transition-colors"
                          >
                            <div className="bg-secondary mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full">
                              <Clock className="text-muted size-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-foreground text-sm font-medium">
                                {formatActionLabel(entry.action)}
                              </p>
                              {detailStr && (
                                <p className="text-muted mt-0.5 truncate text-xs">{detailStr}</p>
                              )}
                            </div>
                            <span className="text-muted mt-0.5 shrink-0 text-xs">
                              {formatDateTime(entry.created_at)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          ) : null}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/** A single row in the detail view. */
function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon className="text-muted size-4 shrink-0" />
      <span className="text-muted w-14 shrink-0">{label}</span>
      <span className="text-foreground flex items-center">{children}</span>
    </div>
  );
}

/** A compact date row for the timeline section. */
function DateRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted flex items-center gap-1.5">
        <Icon className="size-3" />
        {label}
      </span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}
